import { REPORT_CONFIGURATION_NAME } from "../types/constants";
import { useReportStateContext } from "./useReportState";
import { useCallback, useRef } from "react";
import {
  useLazyGetReportQuery,
  useLazyGetReportConfigurationQuery,
} from "services/reportsV3Service";

export const sanitizeReportFilename = (displayName) =>
  displayName.replace(/[/\\,:]/g, "_");

export const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const useExportReport = ({
  savedReport,
  buildReportData,
  buildConfigurationPayload,
  onLoadingChange,
}) => {
  const { hasUnsavedChanges } = useReportStateContext();
  const [fetchReport] = useLazyGetReportQuery();
  const [fetchConfiguration] = useLazyGetReportConfigurationQuery();

  const latestRef = useRef(null);
  latestRef.current = {
    savedReport,
    hasUnsavedChanges,
    buildReportData,
    buildConfigurationPayload,
    fetchReport,
    fetchConfiguration,
    onLoadingChange,
  };

  const handleExport = useCallback(async () => {
    const {
      savedReport,
      hasUnsavedChanges,
      buildReportData,
      buildConfigurationPayload,
      fetchReport,
      fetchConfiguration,
      onLoadingChange,
    } = latestRef.current;

    if (!savedReport?.displayName) return;
    const safeName = sanitizeReportFilename(savedReport.displayName);

    onLoadingChange?.(true);
    try {
      if (!hasUnsavedChanges) {
        const result = await fetchReport(savedReport.internalName);
        if (result.data) {
          const { configuration: _inlineConfig, ...baseReport } = result.data;
          const configResult = await fetchConfiguration({
            reportId: savedReport.internalName,
            configurationName: REPORT_CONFIGURATION_NAME,
          });
          const exportData = {
            report: baseReport,
            ...(configResult.data && Object.keys(configResult.data).length > 0
              ? { configuration: configResult.data }
              : {}),
          };
          downloadJson(exportData, `${safeName}.json`);
        }
        return;
      }

      const reportData = buildReportData();
      const configuration = buildConfigurationPayload();
      const exportData = {
        report: reportData,
        ...(Object.keys(configuration).length > 0 ? { configuration } : {}),
      };
      downloadJson(exportData, `${safeName}-edited.json`);
    } finally {
      onLoadingChange?.(false);
    }
  }, []); // stable — always reads latest values via ref

  return { handleExport };
};
